/**
 * Why a LOGOUT was dispatched without a user-visible trigger (UI button,
 * explicit auth failure with its own error log). Each reason corresponds
 * to a specific code path that fires `eventsStore.dispatch(LOGOUT)` with
 * no console output of its own — the silent paths that made the original
 * STX1-505 repro hard to diagnose.
 *
 *   effects-processor-null-token — pre-graph-effect token check at
 *     EffectsProcessor.process resolved to null/undefined.
 *   effects-processor-token-threw — same gate, but getValidToken threw
 *     instead of resolving (refresh-token invalid, network blip…).
 *   graph-401 — Apollo query/mutation/subscription handler caught a
 *     networkError.statusCode === 401 in GraphProcessor.handleError.
 *   storage-sync-cleared — another tab removed STORAGE_KEY_SESSION; the
 *     `storage` event listener cleared this tab's in-memory _session.
 */
export type SilentLogoutReason =
  | 'effects-processor-null-token'
  | 'effects-processor-token-threw'
  | 'graph-401'
  | 'storage-sync-cleared'

/**
 * Log a silent LOGOUT to the devtools console so the originating path is
 * visible during repro. Matches the existing console-based diagnostic
 * style for other auth-flow events ('No tenants available! Logging out',
 * 'Sign-in method not allowed for tenant…').
 */
export function captureSilentLogout(
  reason: SilentLogoutReason,
  extra?: Record<string, unknown>,
) {
  console.warn('Silent LOGOUT dispatched', { reason, ...extra })
}
